import { useCallback, useEffect, useMemo, useState } from "react";
import { getFirestore, collection, query, where, getDocs, doc, Firestore } from "firebase/firestore";
import { useAuth } from "@/context/AuthContext";
import { CampLocation } from "@/types/campLocation";
import { toast } from "sonner";
import { getCampLocationsFromOrg } from "@/services/getCampLocationsFromOrg";
import { copyPhotosFromLocation } from "@/services/copyPhotosService";
import type { CopyPhotosLocationOption } from "../components/CopyPhotosModal";

interface UseCopyPhotosProps {
	location: CampLocation;
}

interface UseCopyPhotosReturn {
	isModalOpen: boolean;
	isLoading: boolean;
	locationOptions: CopyPhotosLocationOption[];
	canShowCopyButton: boolean;
	showCopyPhotosModal: () => Promise<void>;
	closeCopyPhotosModal: () => void;
	executeCopyPhotos: (sourceLocationId: string) => Promise<void>;
}

const countPhotosForLocation = async (db: Firestore, locationId: string): Promise<number> => {
	const locRef = doc(db, "campLocations", locationId);
	const q = query(collection(db, "campPhotos"), where("usedBy", "array-contains", locRef));
	const snap = await getDocs(q);
	return snap.size;
};

export const useCopyPhotos = ({ location }: UseCopyPhotosProps): UseCopyPhotosReturn => {
	const { currentUser, userProfile } = useAuth();
	const db = getFirestore();

	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [locationOptions, setLocationOptions] = useState<CopyPhotosLocationOption[]>([]);
	const [orgLocationCount, setOrgLocationCount] = useState<number>(0);

	const orgId = useMemo(() => {
		if (!location?.providedByOrg) return "";
		if (typeof location.providedByOrg === "string") return location.providedByOrg;
		if (typeof location.providedByOrg === "object" && "id" in location.providedByOrg) return (location.providedByOrg as any).id || "";
		return "";
	}, [location]);

	useEffect(() => {
		let cancelled = false;
		const loadCount = async () => {
			if (!orgId) return;
			const locs = await getCampLocationsFromOrg(db, orgId);
			if (!cancelled) setOrgLocationCount(locs.length);
		};
		loadCount().catch(() => {});
		return () => { cancelled = true; };
	}, [db, orgId]);

	const canShowCopyButton = orgLocationCount > 1;

	const showCopyPhotosModal = useCallback(async () => {
		if (!currentUser || !location) {
			toast.error("Missing required data for copy photos operation");
			return;
		}
		try {
			setIsLoading(true);
			if (!orgId) {
				toast.error("Location has no organization ID");
				return;
			}
			const allLocs = await getCampLocationsFromOrg(db, orgId);
			const others = allLocs.filter(l => l.id !== location.id);
			const counts = await Promise.all(others.map(l => countPhotosForLocation(db, l.id)));
			const opts: CopyPhotosLocationOption[] = others.map((l, i) => ({
				id: l.id,
				name: l.name || "Untitled location",
				photosCount: counts[i] || 0
			}));
			setLocationOptions(opts);
			setIsModalOpen(true);
		} catch (e) {
			console.error("[useCopyPhotos] Error fetching options", e);
			toast.error("Failed to fetch locations to copy from");
		} finally {
			setIsLoading(false);
		}
	}, [currentUser, location, orgId, db]);

	const closeCopyPhotosModal = useCallback(() => {
		setIsModalOpen(false);
		setLocationOptions([]);
	}, []);

	const executeCopyPhotos = useCallback(async (sourceLocationId: string) => {
		if (!currentUser || !location) {
			toast.error("Missing required data for copy photos operation");
			return;
		}
		try {
			setIsLoading(true);
			const result = await copyPhotosFromLocation(
				db,
				sourceLocationId,
				location.id,
				currentUser.uid,
				userProfile?.locationAccessGroupId
			);
			if (result.success) {
				toast.success(`Successfully copied ${result.copiedCount} photo${result.copiedCount !== 1 ? "s" : ""}!`, {
					description: "The page will refresh to show the updated photos list."
				});
				closeCopyPhotosModal();
				setTimeout(() => window.location.reload(), 1500);
			} else {
				toast.error(`Copy photos failed: ${result.errors.join(", ")}`);
			}
		} catch (e) {
			console.error("[useCopyPhotos] Copy error", e);
			toast.error("Failed to copy photos");
		} finally {
			setIsLoading(false);
		}
	}, [currentUser, location, db, closeCopyPhotosModal, userProfile?.locationAccessGroupId]);

	return {
		isModalOpen,
		isLoading,
		locationOptions,
		canShowCopyButton,
		showCopyPhotosModal,
		closeCopyPhotosModal,
		executeCopyPhotos
	};
};
